/** Member-selected metadata, independent of sign-in and delivery. */
export type EmailCategory = 'UNKNOWN' | 'PERSONAL' | 'WORK';

/** One address attached to the member's existing account. */
export interface MemberEmail {
  id: string;
  email: string;
  slot: 'PRIMARY' | 'SECONDARY' | 'PENDING';
  category: EmailCategory;
  verified: boolean;
  replacesId: string | null;
}

/** Expected API failures are presented to the admin without throwing. */
export type EmailResult<T> = { ok: true; value: T } | { ok: false; kind: 'validation' | 'request'; message: string };

/** Parse the HTTP boundary using TS 2.4, which predates unknown. */
// oxlint-disable-next-line no-explicit-any -- SAFETY: Every consumed field is checked before use.
export function parseMemberEmails(value: any): EmailResult<MemberEmail[]> {
  if (!Array.isArray(value)) {
    return { ok: false, kind: 'request', message: 'Could not read email addresses. Please retry.' };
  }
  const addresses: MemberEmail[] = [];
  for (const item of value) {
    if (!item || typeof item.id !== 'string' || typeof item.email !== 'string' ||
        ['PRIMARY', 'SECONDARY', 'PENDING'].indexOf(item.slot) < 0 ||
        ['UNKNOWN', 'PERSONAL', 'WORK'].indexOf(item.category) < 0 || typeof item.verified !== 'boolean' ||
        !(item.replacesId === null || typeof item.replacesId === 'string')) {
      return { ok: false, kind: 'request', message: 'Could not read email addresses. Please retry.' };
    }
    addresses.push({ id: item.id, email: item.email, slot: item.slot, category: item.category,
      verified: item.verified, replacesId: item.replacesId });
  }
  if (addresses.filter(address => address.slot === 'PRIMARY').length !== 1) {
    return { ok: false, kind: 'request', message: 'Could not read the primary email. Please retry.' };
  }
  return { ok: true, value: addresses };
}

/** Translate CHttp errors, including field validation errors, into safe display text. */
// oxlint-disable-next-line no-explicit-any -- SAFETY: CHttp exposes untyped responses; all displayed fields are checked.
export function memberEmailError(error: any): EmailResult<never> {
  const messages: string[] = [];
  if (error && error.status === 400 && Array.isArray(error.errors)) {
    for (const field of error.errors) {
      if (field && typeof field.message === 'string') { messages.push(field.message); }
    }
  }
  return { ok: false, kind: messages.length ? 'validation' : 'request',
    message: messages.join(' ') || 'Could not complete the email request. Please retry.' };
}



// WEBPACK FOOTER //
// /home/runner/_work/monorepo/monorepo/admin-portal-old/src/app/member/member-emails.ts